import * as React from 'react';
import { Page } from '@src/components';
import { DateRangePicker, ClientDropdown } from '@src/components'; 
import { useOverview } from "@src/context/dashboard/Overview";  
import { Grid, Card, CardContent, Typography, Divider } from "@mui/material"; 
import { useMemo } from "react";

export default function DashboardOverviewPage() {
  const { 
    metrics = [],  
    chartData = [], 
    isLoading, 
    isError,
    clientId, 
    setClientId, 
    startDate, 
    setStartDate, 
    endDate, 
    setEndDate 
  } = useOverview();

  console.log("Client ID:", clientId);
  console.log("Fetched metrics:", metrics);  // Debugging the fetched metrics
  
  // Check if API data is being populated into metrics
  if (metrics.length === 0) {
    console.log("No metrics available. Check if the metrics are being set correctly.");
  }

  const handleDateRangeChange = (e) => {
    const { startDate, endDate } = e.selection;
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const handleClientChange = (clientId) => {
    console.log('Client selected:', clientId);  
    setClientId(clientId);
  };

  // Display metrics in cards (widgets)
  const renderMetricWidgets = () => {
    if (!metrics || metrics.length === 0) {
      return <Typography>No metrics data available</Typography>;
    }

    return metrics.map((metric, index) => (
      <Grid item xs={12} sm={6} md={3} key={index}>
        <Card>
          <CardContent>
            <Typography variant="h6" color="textSecondary">{metric.label}</Typography>
            <Typography variant="h4">{metric.value}</Typography>
          </CardContent>
        </Card>
      </Grid>
    ));
  };

  // Create cards for specific metrics
  const renderSpecialCards = () => {
    return (
      <Grid container spacing={3}>
        {/* Total Spend & Total Leads - Combined in One Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="textSecondary">Total Leads & CPL</Typography>
              <Typography variant="h4">{metrics.total_leads_sum || 0}</Typography>
              <Divider />
              <Typography variant="h6" color="textSecondary">Cost per Lead</Typography>
              <Typography variant="h4">${metrics.cost_per_lead_avg || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Appointments & Total Issues - Combined in One Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="textSecondary">Total Appointments & Issues</Typography>
              <Typography variant="h4">{metrics.booked_appointments_sum || 0}</Typography>
              <Divider />
              <Typography variant="h6" color="textSecondary">Total Issues</Typography>
              <Typography variant="h4">{metrics.total_issues || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Net Deals & Total Net Revenue - Combined in One Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="textSecondary">Total Net Deals & Revenue</Typography>
              <Typography variant="h4">{metrics.total_net_deals || 0}</Typography>
              <Divider />
              <Typography variant="h6" color="textSecondary">Total Net Revenue</Typography>
              <Typography variant="h4">${metrics.total_net_revenue || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  return (
    <Page title={`Client Dashboard - ${metrics.client__name}`} isLoading={isLoading} errorCode={isError ? 500 : null}>
      {/* Filters: Client Dropdown and Date Range Picker */}
      <div className="filters">
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateRangeChange}
        />
        <ClientDropdown value={clientId} onChange={handleClientChange} />
      </div>

      {/* Display Metrics as Widgets (Cards) */}
      <Grid container spacing={3}>
        {renderMetricWidgets()}
      </Grid>

      {/* Display Special Cards (Total Spend, Appointments, etc.) */}
      {renderSpecialCards()}

      {/* Display Chart Data (example) */}
      <div className="chart-data">
        {chartData && chartData.length > 0 ? (
          chartData.map((dataItem) => (
            <div key={dataItem.uuid}>
              <h3>{dataItem.client__name}</h3>
              <p>Total Spend: {dataItem.total_spend_sum}</p>
            </div>
          ))
        ) : (
          <p>No chart data available</p>
        )}
      </div>
    </Page>
  );
}
